import { Injectable } from '@angular/core';
import { NgbDateAdapter, NgbDateParserFormatter, NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';

export class JissuDateTimeUtils {
  public static date2NgbDateStruct(date: Date): NgbDateStruct {
    return {
      day: dayjs(date).get('date'),
      month: dayjs(date).get('month') + 1,
      year: dayjs(date).get('year'),
    };
  }

  public static ngbDateStruct2date(date: NgbDateStruct | null): Date | null {
    return date ? new Date(date.year, date.month - 1, date.day) : null;
  }

  public static dateToLocalDateString(date: Date | string | undefined | null): string | undefined {
    if (!date) {
      return undefined;
    }
    const result = dayjs(date).format('YYYY-MM-DD');
    return result && result !== 'Invalid Date' ? result : undefined;
  }

  public static validDate(date: any): boolean {
    return !window.isNaN(date) && date instanceof Date;
  }

  public static ngbTimeStruct2ISODuration(time: NgbTimeStruct | null): string {
    return time ? 'PT' + time?.hour + 'H' + time?.minute + 'M' + time?.second + 'S' : 'PT0H0M';
  }

  public static isoDuration2ngbTimeStruct(duration: string | null | undefined): NgbTimeStruct | null {
    if (!duration) {
      return null;
    }

    const regex = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;

    const match = duration?.match(regex);

    if (!match) {
      return null;
    }

    return {
      hour: +match[1] || 0,
      minute: +match[2] || 0,
      second: +match[3] || 0,
    };
  }

  public static date2ngbTimeStruct(date: Date | string | undefined): NgbTimeStruct {
    if (!date) {
      return {
        hour: 0,
        minute: 0,
        second: 0,
      };
    }

    const solidDate = dayjs(date);

    return {
      hour: solidDate.get('hour') || 0,
      minute: solidDate.get('minute') || 0,
      second: solidDate.get('second') || 0,
    };
  }

  public static mergeDateTime(date: Date | undefined, time: NgbTimeStruct | undefined): Date {
    const result = date ? new Date(date) : new Date();

    result.setHours(time?.hour || 0);
    result.setMinutes(time?.minute || 0);
    result.setSeconds(time?.second || 0);

    return result;
  }

  public static findAndFormatIsoDates(str: string, formatCb = this.formatDateToShort): string {
    return str.replace(/\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/g, (date) => {
      return formatCb(date);
    });
  }

  public static formatDateToShort(date: string | Date | undefined): string {
    return date ? dayjs(date).format('DD.MM.YYYY hh:mm').toString() : '';
  }

  public static formatDateToChat(date: string | Date | undefined): string {
    if (date == null) {
      return '';
    } else if (dayjs(date).isSame(dayjs(), 'day')) {
      return 'Dnes o ' + dayjs(date).format('HH:mm');
    } else if (dayjs(date).isSame(dayjs().subtract(1, 'day'), 'day')) {
      return 'Včera o ' + dayjs(date).format('HH:mm');
    } else {
      return dayjs(date).format('DD.MM.YYYY HH:mm');
    }
  }
}

/**
 * This Service handles how the date is represented in scripts i.e. ngModel.
 */
@Injectable()
export class CustomAdapter extends NgbDateAdapter<Date> {
  fromModel(value: Date | null): NgbDateStruct | null {
    if (value) {
      const date = JissuDateTimeUtils.date2NgbDateStruct(value);
      return date;
    }
    return null;
  }

  toModel(date: NgbDateStruct | null): Date | null {
    return JissuDateTimeUtils.ngbDateStruct2date(date);
  }
}

/**
 * This Service handles how the date is rendered and parsed from keyboard i.e. in the bound input field.
 */
@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = '.';

  parse(value: string): NgbDateStruct | null {
    if (value) {
      const date = value.split(this.DELIMITER);
      return {
        day: parseInt(date[0], 10),
        month: parseInt(date[1], 10),
        year: parseInt(date[2], 10),
      };
    }
    return null;
  }

  /** Deň a mesiac s nulou (07.08.2026) - rovnaký formát, aký používa zvyšok portálu (ADO 57421, Figma #37). */
  format(date: NgbDateStruct | null): string {
    if (!date) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return pad(date.day) + this.DELIMITER + pad(date.month) + this.DELIMITER + date.year;
  }
}
